import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';

import { useHistory } from 'react-router-dom';

import nofilterMic from '@/images/org-logo-nofilter.svg';
import type { Account } from 'mastodon/models/account';

interface Props {
  account?: Account;
}

// Verified-org badge — small "shiny" disc next to the display name. Shows the
// org's uploaded image (or the bundled NoFilter mic-guy as default). The
// shimmer + gold ring are pure CSS (see .org-badge in components.scss); the
// image itself sits inside a circular mask.
//
// If the member has been handed a custom badge by an org admin, a second disc
// is rendered right after the org badge (additive — the org default always
// shows, the assigned badge is extra).
export const OrgBadge: FC<Props> = ({ account }) => {
  const history = useHistory();
  const orgs = account?.get('verified_orgs');
  const org = orgs?.first();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!org) return;
      event.preventDefault();
      event.stopPropagation();
      history.push(`/organizations/${org.get('slug')}`);
    },
    [history, org],
  );

  if (!org) return null;

  const src = org.get('badge_url') ?? nofilterMic;
  const assigned = org.get('assigned_badge');

  return (
    <>
      <button
        type='button'
        className='org-badge'
        onClick={handleClick}
        title={`Verified: ${org.get('name')}`}
        aria-label={`Verified member of ${org.get('name')}`}
      >
        <img className='org-badge__img' src={src} alt='' />
      </button>
      {assigned && (
        <button
          type='button'
          className='org-badge org-badge--assigned'
          onClick={handleClick}
          title={`${org.get('name')}: ${assigned.label}`}
          aria-label={`${assigned.label} — ${org.get('name')}`}
        >
          {assigned.image_url ? (
            <img className='org-badge__img' src={assigned.image_url} alt='' />
          ) : (
            <span className='org-badge__label'>{assigned.label.charAt(0)}</span>
          )}
        </button>
      )}
    </>
  );
};
